/**
 * @fileoverview RFQ (Request for Quote) Management Hooks
 * 
 * Comprehensive hooks for fetching and managing RFQs with advanced filtering,
 * bid exclusion, and expired RFQ handling. Provides both list and detail views
 * with full job context, specifications, and vendor invitation data.
 * 
 * RFQ Lifecycle:
 * - Open: Accepting bids from vendors
 * - Closed: Deadline passed or manually closed
 * - Awarded: Job has been awarded to a winning bid
 * 
 * Key Features:
 * - Automatic expiration management (closes expired RFQs)
 * - Multi-criteria filtering (category, search, user bids)
 * - Specification and quantity enrichment
 * - Vendor invitation tracking
 * - Decline tracking (excludes declined RFQs)
 * 
 * Expiration Handling:
 * Both hooks invoke 'close-expired-rfqs' edge function before fetching to ensure
 * RFQ status is current. This is a non-blocking operation - failures are logged
 * but don't prevent data fetching.
 * 
 * @module useRFQs
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';
import { RFQ, JobCategory } from '@/lib/types/job';

/**
 * Options for filtering RFQs
 */
interface UseRFQsOptions {
  /** Search RFQs by job title or description */
  search?: string;
  /** Filter RFQs by job category */
  category?: JobCategory;
  /** Exclude RFQs where the current user has already submitted a bid */
  excludeUserBids?: boolean;
}

/**
 * Hook for fetching and managing open RFQs with filtering
 * 
 * Fetches open RFQs with associated job data, specifications, quantity options,
 * bids, and vendor invitations. Automatically closes expired RFQs before fetching
 * to ensure status accuracy.
 * 
 * Query Behavior:
 * - Filters to 'open' status only
 * - Enriches with full job details and organization
 * - Fetches specifications and quantity options separately (performance)
 * - Orders by creation date descending
 * 
 * Filtering Logic:
 * - Category filter: Applied client-side after data fetch
 * - Search: Client-side text search on title/description
 * - Exclude user bids: Filters out RFQs where current user has submitted a bid
 * - Exclude declines: Filters out RFQs the user has explicitly declined
 * 
 * Specification Enrichment:
 * For each RFQ, fetches job_specifications and their associated
 * rfq_specification_quantity_options to provide complete bid requirements.
 * 
 * Error Handling:
 * - Expired RFQ closure failures are logged but don't block fetch
 * - Specification fetch errors are logged but don't fail entire query
 * - Detailed error logging with stack traces for debugging
 * 
 * @param options - Filtering options
 * @returns Object with RFQs array, loading state, error, and refetch function
 * 
 * @example
 * ```typescript
 * // Vendor browsing available RFQs
 * const { rfqs, loading, refetch } = useRFQs({
 *   category: 'print_materials',
 *   search: 'business cards',
 *   excludeUserBids: true // Don't show RFQs I already bid on
 * });
 * ```
 * 
 * @example
 * ```typescript
 * // Client viewing all open RFQs for their category
 * const { rfqs, loading } = useRFQs({
 *   category: 'promotional_products'
 * });
 * ```
 */
export const useRFQs = (options: UseRFQsOptions = {}) => {
  const [rfqs, setRFQs] = useState<(RFQ & { job: any; bids?: any[] })[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRFQs = async () => {
    try {
      setLoading(true);
      
      // Get current user if we need to exclude their bids
      let currentUserId: string | null = null;
      if (options.excludeUserBids) {
        const { data: { user } } = await supabase.auth.getUser();
        currentUserId = user?.id || null;
      }
      
      // First close any expired RFQs (don't fail if this times out)
      try {
        await invokeEdgeFunction('close-expired-rfqs', {
          callerOperation: 'useRFQs.fetchRFQs',
        });
      } catch (edgeFunctionError) {
        console.warn('Edge function close-expired-rfqs failed, continuing anyway:', edgeFunctionError);
        // Don't throw - we can still fetch RFQs even if closing expired ones fails
      }
      
      let query = supabase
        .from('rfqs')
        .select(`
          *,
          job:jobs(
            *,
            organization:organizations(
              id,
              name,
              organization_profiles(industry)
            )
          ),
          bids(id, vendor_user_id, status),
          invitations:rfq_vendor_invitations(id, vendor_user_id, invitation_status),
          questions:rfq_questions(id),
        rfq_targeting_criteria(
            min_rating,
            min_completed_jobs,
            min_verification_level,
            required_certification_type_ids
          )
        `)
        .eq('status', 'open')
        .order('created_at', { ascending: false });
      
      const { data: rfqsData, error: rfqError } = await query;
      
      if (rfqError) throw new Error(rfqError.message);
      
      // Fetch job specifications and quantities separately for each RFQ
      const rfqsWithSpecs = await Promise.all(
        (rfqsData || []).map(async (rfq: any) => {
          if (!rfq.job?.id) return rfq;
          
          const { data: specs, error: specsError } = await supabase
            .from('job_specifications')
            .select(`
              id,
              specification_name,
              content
            `)
            .eq('job_id', rfq.job.id);
          
          if (specsError) {
            console.warn('Skipping specifications for RFQ due to error:', { rfqId: rfq.id, specsError });
            return rfq;
          }
          
          // Fetch quantities for each specification
          if (specs && specs.length > 0) {
            const specsWithQuantities = await Promise.all(
              specs.map(async (spec: any) => {
                const { data: quantities, error: qtyError } = await supabase
                  .from('rfq_specification_quantity_options')
                  .select('quantity_option, unit')
                  .eq('rfq_id', rfq.id)
                  .eq('specification_id', spec.id)
                  .order('quantity_option', { ascending: true });
                
                if (qtyError) {
                  console.warn('Skipping quantities for spec due to error:', { rfqId: rfq.id, specId: spec.id, qtyError });
                  return { ...spec, quantities: [] };
                }
                
                return {
                  ...spec,
                  quantities: (quantities || []).map(q => ({
                    quantity: q.quantity_option,
                    unit: q.unit,
                  })),
                };
              })
            );
            
            return {
              ...rfq,
              job: {
                ...rfq.job,
                specifications: specsWithQuantities
              }
            };
          }
          
          return rfq;
        })
      );

      let filteredData = rfqsWithSpecs || [];
      
      // Apply category filter (client-side)
      if (options.category) {
        filteredData = filteredData.filter(rfq => 
          rfq.job?.category === options.category
        );
      }

    // Exclude RFQs where user has already bid
    if (options.excludeUserBids && currentUserId) {
      filteredData = filteredData.filter(rfq => 
        !rfq.bids?.some((bid: any) => bid.vendor_user_id === currentUserId)
      );
    }

    // Exclude RFQs where user has declined
    if (options.excludeUserBids && currentUserId) {
      const { data: declines } = await supabase
        .from('rfq_declines')
        .select('rfq_id')
        .eq('vendor_user_id', currentUserId);
      
      const declinedRfqIds = new Set(declines?.map(d => d.rfq_id) || []);
      filteredData = filteredData.filter(rfq => !declinedRfqIds.has(rfq.id));
    }

      // Apply search filter (client-side for now)
      if (options.search) {
        const searchLower = options.search.toLowerCase();
        filteredData = filteredData.filter(rfq => 
          rfq.job && (
            rfq.job.title.toLowerCase().includes(searchLower) ||
            rfq.job.description.toLowerCase().includes(searchLower)
          )
        );
      }

      setRFQs(filteredData as any);
      setError(null);
    } catch (err) {
      console.error('Error fetching RFQs:', err);
      console.error('Error details:', {
        message: err instanceof Error ? err.message : 'Unknown error',
        stack: err instanceof Error ? err.stack : undefined,
        errorObject: err
      });
      setError(err instanceof Error ? err.message : 'Failed to fetch RFQs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRFQs();
  }, [options.search, options.category]);

  return { rfqs, loading, error, refetch: fetchRFQs };
};

/**
 * Hook for fetching a single RFQ by its public ID
 * 
 * Fetches complete RFQ details including:
 * - Full RFQ record (deadline, status, visibility)
 * - Associated job with organization details
 * - RFQ addenda (amendments and clarifications)
 * - Vendor questions and answers
 * - Vendor invitations with status
 * - Public RFQ criteria (if public visibility)
 * 
 * Expiration Check:
 * Like useRFQs, this hook invokes close-expired-rfqs before fetching
 * to ensure the RFQ status is current. Failures are logged but non-blocking.
 * 
 * Public Criteria:
 * If the RFQ has visibility_type='public', fetches targeting criteria
 * (distance, certifications, ratings) and attaches as public_rfq_criteria.
 * 
 * @param rfqId - The public ID of the RFQ to fetch
 * @returns Object with RFQ data (including job, addenda, questions), loading state, and error
 * 
 * @example
 * ```typescript
 * // Vendor viewing RFQ details before bidding
 * const { rfq, loading, error } = useRFQ('rfq-abc-123');
 * 
 * if (rfq) {
 *   console.log('Job:', rfq.job.title);
 *   console.log('Deadline:', rfq.deadline);
 *   console.log('Addenda:', rfq.addenda.length);
 *   console.log('Questions:', rfq.questions.length);
 * }
 * ```
 * 
 * @example
 * ```typescript
 * // Check if vendor is invited
 * const { rfq } = useRFQ(rfqId);
 * const isInvited = rfq?.invitations?.some(
 *   inv => inv.vendor_user_id === currentUserId && inv.invitation_status === 'sent'
 * );
 * ```
 */
export const useRFQ = (rfqId: string) => {
  const [rfq, setRFQ] = useState<(RFQ & { job: any }) | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRFQ = async () => {
      if (!rfqId) return;

      try {
        setLoading(true);
        
        // First close any expired RFQs (don't fail if this times out)
        try {
          await invokeEdgeFunction('close-expired-rfqs', {
            callerOperation: 'useRFQ.fetchRFQ',
          });
        } catch (edgeFunctionError) {
          console.warn('Edge function close-expired-rfqs failed, continuing anyway:', edgeFunctionError);
          // Don't throw - we can still fetch RFQ even if closing expired ones fails
        }
        
      const { data, error } = await supabase
        .from('rfqs')
        .select(`
          *,
          job:jobs(
            *,
            organization:organizations(
              id,
              name
            )
          ),
          addenda:rfq_addenda(*),
          questions:rfq_questions(*),
          invitations:rfq_vendor_invitations(id, vendor_user_id, invitation_status)
        `)
        .eq('public_id', rfqId)
        .single();
      
      // Fetch public RFQ criteria if needed
      if (data && data.visibility_type === 'public') {
        const { data: criteria } = await supabase
          .from('rfq_targeting_criteria')
          .select('*')
          .eq('rfq_id', data.id)
          .maybeSingle();
        
        if (criteria) {
          (data as any).public_rfq_criteria = criteria;
        }
      }

        if (error) throw error;

        setRFQ(data as any);
        setError(null);
      } catch (err) {
        console.error('Error fetching RFQ:', err);
        console.error('Error details:', {
          message: err instanceof Error ? err.message : 'Unknown error',
          stack: err instanceof Error ? err.stack : undefined,
          errorObject: err
        });
        setError(err instanceof Error ? err.message : 'Failed to fetch RFQ');
      } finally {
        setLoading(false);
      }
    };

    fetchRFQ();
  }, [rfqId]);

  return { rfq, loading, error };
};